import { useAuth } from "@clerk/clerk-react";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AgentSession, AgentSessionType } from "schemas/dashboard";
import { routes } from "schemas/routes";

import { fetcherAuth } from "utils/api";

import { AlertContext } from "./Alert";
import { OrganizationUserContext } from "./Organization";

interface AgentSessionsContextType {
  agentSessions: AgentSession[] | null;
  selectedAgentSessionId: number | null;
  setAgentSessions: Dispatch<SetStateAction<AgentSession[] | null>>;
  addNewAgentSession: (session: AgentSession) => void;
  fetchAgentSessions: () => void;
}

const AgentSessionsContext = createContext<
  AgentSessionsContextType | undefined
>(undefined);

export const useAgentSessions = () => {
  const context = useContext(AgentSessionsContext);
  if (!context) {
    throw new Error(
      "useAgentSessions must be used within an AgentSessionsProvider",
    );
  }
  return context;
};

interface AgentSessionsProviderProps {
  children: ReactNode;
}

export const AgentSessionsProvider = ({
  children,
}: AgentSessionsProviderProps) => {
  const [agentSessions, setAgentSessions] = useState<AgentSession[] | null>(
    null,
  );

  const { setErrorAlert } = useContext(AlertContext);

  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const location = useLocation();
  const navigate = useNavigate();

  const fetchAgentSessions = async () => {
    if (!currentOrg?.id) return;

    try {
      const { sessions } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/agent-sessions?types=${AgentSessionType.outreach}&types=${AgentSessionType.analytics}&types=${AgentSessionType.manage_inbox}`,
        "GET",
      );
      setAgentSessions(sessions || []);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const addNewAgentSession = (session: AgentSession) => {
    setAgentSessions((prev) => [session, ...(prev || [])]);
  };

  useEffect(() => {
    if (currentOrg?.id) {
      fetchAgentSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const urlSessionId = useMemo(() => {
    const match = location.pathname.match(/\/home\/(\d+)\/?/);
    if (!match) return null;
    const parsed = parseInt(match[1], 10);
    return Number.isFinite(parsed) ? parsed : null;
  }, [location.pathname]);

  const selectedAgentSessionId = useMemo(() => {
    if (!urlSessionId) return null;
    if (agentSessions === null) {
      return urlSessionId;
    }
    return agentSessions.some((s) => s.id === urlSessionId)
      ? urlSessionId
      : null;
  }, [urlSessionId, agentSessions]);

  useEffect(() => {
    if (urlSessionId && selectedAgentSessionId === null) {
      navigate(`/${routes.home}${location.search}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSessionId, selectedAgentSessionId]);

  return (
    <AgentSessionsContext.Provider
      value={{
        agentSessions,
        selectedAgentSessionId,
        setAgentSessions,
        addNewAgentSession,
        fetchAgentSessions,
      }}
    >
      {children}
    </AgentSessionsContext.Provider>
  );
};
